import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useDownloadApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { Reagent, ReagentForm, ReagentSearchForm } from 'entities/reagent'

type ReagentsResponse = PagingResponse & {
    results: Reagent[]
}

export function useFetchReagentsApi(searchForm?: Form<ReagentSearchForm>): IndexApiSet<ReagentsResponse> & { execute: () => void } {
    const apiPath = 'reagents/'
    const api = useIndexApi<ReagentsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object?.page || 1, perPage: searchForm?.object?.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function useDownloadCsvReagentsApi(searchForm: Form<ReagentSearchForm>): ApiSet<BaseResponse> & { execute: () => void } {
    const apiPath = 'reagents/csv/'
    const api = useDownloadApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (): void => {
        api.execute(apiPath, 'reagent', searchForm.object)
    }

    return { ...api, execute: execute }
}

export function usePostReagentApi(): ApiSet<BaseResponse> & { execute: (form: Form<ReagentForm>) => void } {
    const apiSet = usePostApi<BaseResponse, ReagentForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<ReagentForm>) => {
        const apiPath = `reagents/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutReagentApi(): ApiSet<BaseResponse> & { execute: (form: Form<ReagentForm>) => void } {
    const apiSet = usePutApi<BaseResponse, ReagentForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<ReagentForm>) => {
        const apiPath = `reagents/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutReagentsApi(): ApiSet<BaseResponse> & { execute: (form: Form<ReagentForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, ReagentForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<ReagentForm[]>) => {
        const apiPath = `reagents/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteReagentApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `reagents/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
